import { createNewSession } from 'flow/actions/chat-actions';
import { joinMeeting, setDevicesInfo } from 'flow/actions/meeting-actions';
import {
    notifyVideoCall,
    handleVideo2OutgoingCall,
} from 'flow/actions/video2-actions';
import { getChatUserKeyForExtensionWithKey } from 'flow/selectors/ExtensionSelectors';
import { selectNameInApp } from 'flow/selectors/selectors';
import { ChatSessionKey, ExtensionKey } from 'flow/types/EntityKeys';
import { useAppDispatch, useAppSelector } from 'flow/hooks/useRedux';
import { getMeetingCode } from 'flow/utils/meeting-utils';
import { MeetingType } from 'flow/utils/meeting-constants';

interface VideoHook {
    joinVideoCall: (meetingCode: string) => Promise<void>;
    startVideoCall: (chatSessionKey?: ChatSessionKey) => void;
    notifyCurrentVideoCall: (
        extensionKey: ExtensionKey,
        chatSessionKey: ChatSessionKey,
    ) => void;
}

const useVideo = (extensionKey: ExtensionKey = null): VideoHook => {
    const dispatch = useAppDispatch();

    const nameInApp = useAppSelector(selectNameInApp);

    const chatUserKey = useAppSelector((state) =>
        getChatUserKeyForExtensionWithKey(state, extensionKey),
    );

    const joinVideoCall = async (meetingCode: string) => {
        await dispatch(setDevicesInfo());
        dispatch(joinMeeting(nameInApp, meetingCode));
    };

    const startVideoCallBySession = (chatSessionKey: ChatSessionKey) => {
        dispatch(getMeetingCode(chatSessionKey, MeetingType.INSTANT.key)).then(
            (meetingCode: string) => {
                dispatch(notifyVideoCall(extensionKey, meetingCode));
                dispatch(handleVideo2OutgoingCall(extensionKey, meetingCode));
            },
        );
    };

    const startVideoCall = (chatSessionKey?: ChatSessionKey) => {
        if (chatSessionKey) {
            startVideoCallBySession(chatSessionKey);
        } else {
            dispatch(createNewSession(chatUserKey, startVideoCallBySession));
        }
    };

    const notifyCurrentVideoCall = (
        extensionKey: ExtensionKey,
        chatSessionKey: ChatSessionKey,
        meetingCode: string = null,
    ) => {
        if (meetingCode) {
            dispatch(notifyVideoCall(extensionKey, meetingCode));
        } else {
            dispatch(
                getMeetingCode(chatSessionKey, MeetingType.INSTANT.key),
            ).then((meetingCode: string) => {
                dispatch(notifyVideoCall(extensionKey, meetingCode));
            });
        }
    };

    return { joinVideoCall, startVideoCall, notifyCurrentVideoCall };
};

export default useVideo;
